import { URL } from 'node:url';
import { exit } from 'node:process';
import { DeploymentOperation } from '@wix/ambassador-velo-apps-v1-app/types';
import {
  getDeploymentPipelineDescription,
  publishDeployment,
} from '@wix/ambassador-document-management-editor-deployments-v1-deployment/http';
import { DeploymentPipelinesStatus } from '@wix/ambassador-document-management-editor-deployments-v1-deployment/types';
import prompts from 'prompts';
import { isType } from 'variant';
import chalk from 'chalk';
import { writeCrashReport } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import type { CommandServices } from '@wix/cli-command-contract';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getDefaultBranch } from '../git/index.js';
import {
  deployGridApp,
  DeploymentSource,
  getDeploymentStatus,
} from '../gridapp/deployApp.js';
import type { ProjectModel } from '../model.js';
import { getLatestRevision } from '../pages-types-client/index.js';
import { httpRequest } from '../http-request.js';
import { isSiteNotPublishedError } from '../utils.js';
import { createLogger } from '../logger.js';
import { createI18nT } from '../i18n.js';
import { authenticate } from '../authentication.js';

const toBaseSiteUrl = (fullUrl: string) => {
  const url = new URL(fullUrl);
  return `${url.protocol}//${url.host}${url.pathname}`;
};

export async function publish(
  model: ProjectModel,
  { biLogger, errorReporter }: CommandServices,
  options: {
    source?: 'local' | 'remote';
    approvePreview?: true;
    force?: true;
  }
) {
  const authState = await authenticate({
    metaSiteId: model.metaSiteId,
    biLogger,
    errorReporter,
  });
  const t = createI18nT();
  const logger = createLogger({ t });

  const defaultBranchName = await getDefaultBranch((e) =>
    writeCrashReport(getDebugLogFilePath(model.projectFolder), e, new Date())
  );

  const whatToPublish = options.source
    ? { value: options.source }
    : await prompts(
        {
          type: 'select',
          name: 'value',
          message: logger.t('publish_command.prompt_what_to_publish'),
          choices: [
            {
              title: chalk.cyan(
                defaultBranchName
                  ? logger.t(
                      'publish_command.prompt_what_to_publish_choice_remote',
                      {
                        branchName: defaultBranchName,
                      }
                    )
                  : logger.t(
                      'publish_command.prompt_what_to_publish_choice_remote_fallback'
                    )
              ),
              value: 'remote',
            },
            {
              title: chalk.cyan(
                logger.t('publish_command.prompt_what_to_publish_choice_local')
              ),
              value: 'local',
            },
          ],
        },
        {
          onCancel: () => {
            logger.logAborting();
            exit(130 /* SIGINT */);
          },
        }
      );

  const deploymentSource =
    whatToPublish.value === 'local'
      ? DeploymentSource.local()
      : DeploymentSource.remote();

  let preview;
  const logPreviewed = logger.logPreviewing();

  try {
    preview = await deployGridApp({
      model,
      operation: DeploymentOperation.RC,
      source: deploymentSource,
      authState,
    });

    logPreviewed.success();
  } catch (err) {
    if (isSiteNotPublishedError(err)) {
      logPreviewed.stop();
      logger.logPreviewUnsupported();
    } else {
      logPreviewed.fail();
      throw err;
    }
  }

  const latestRevision = await getLatestRevision({ authState, logger });
  const currentRevision = preview?.deployedRevision ?? model.revision;

  logger.logPublishRevision({
    branchName: defaultBranchName,
    isLocalDeployment: isType(deploymentSource, DeploymentSource.local),
    currentRevision,
    latestRevision,
    preview,
  });

  const shouldPublish = options.approvePreview
    ? { value: true }
    : await prompts({
        type: 'confirm',
        name: 'value',
        message: logger.t('publish_command.prompt_continue_with_publish'),
        initial: true,
      });

  const publish = shouldPublish.value;

  if (publish) {
    const logDeployed = logger.logDeploying();

    // Use preview as an original deployment, but if preview wasn't created, created GA deploument.
    // We need this logic in order to support limitation related to publishing unpublished sites.
    const deployment =
      preview ??
      (await deployGridApp({
        model,
        authState,
        operation: DeploymentOperation.GA,
        source: deploymentSource,
      }));

    const { deploymentId, deploymentUrl, isPublishPipelineDeployment } =
      deployment;

    let deploymentStatus;
    try {
      deploymentStatus = !isPublishPipelineDeployment
        ? DeploymentPipelinesStatus.SUCCESS
        : await getDeploymentStatus({
            deploymentId,
            authState,
          });
    } catch (err) {
      logDeployed.fail();
      throw err;
    }

    if (deploymentStatus === DeploymentPipelinesStatus.SUCCESS) {
      logDeployed.success();
    }

    let skipPipelineCheck;
    if (deploymentStatus === DeploymentPipelinesStatus.ERROR) {
      logDeployed.fail();

      try {
        const { data } = await httpRequest(
          {
            type: 'editor',
            authState,
          },
          getDeploymentPipelineDescription({
            deploymentId: deployment.deploymentId,
            deploymentPipelinesIds: ['5c696513-2584-4f19-ae0f-a559dd649482'], // Velo Pipeline ID
          }),
          { retries: 2 }
        );

        if (data.pipelinesDescription?.length) {
          const { pipelinesDescription } = data;
          logger.logPipelineErrorInformation({ pipelinesDescription });
        }
      } catch (err) {
        throw new CliError({
          code: CliErrorCode.FailedToGetDeploymentPipelineDescription(),
          cause: err,
        });
      }

      logger.logForceDeployWarning();

      const shouldSkipPipelineCheck = options.force
        ? { value: true }
        : await prompts({
            type: 'confirm',
            name: 'value',
            message: t('publish_command.prompt_publish_with_error'),
            initial: true,
          });

      skipPipelineCheck = shouldSkipPipelineCheck.value;
    }

    if (
      deploymentStatus === DeploymentPipelinesStatus.SUCCESS ||
      skipPipelineCheck
    ) {
      const logPublished = logger.logPublishing();
      try {
        await httpRequest(
          {
            type: 'editor',
            authState,
          },
          publishDeployment({
            deploymentId,
            skipPipelineCheck,
          }),
          { retries: 2 }
        );
      } catch (err) {
        logPublished.fail();
        throw new CliError({
          code: CliErrorCode.FailedToPublishDeployment(),
          cause: err,
        });
      }

      logPublished.success({
        siteUrl: toBaseSiteUrl(deploymentUrl),
      });
    }
  } else {
    logger.logPublishAborted();
  }
}
